<template>
  <div class="border-input" :class="{ 'border-input--off': isOff }">
    <div class="border-input__controls">
      <div class="border-input__head">
        <div class="border-input__styles">
          <button
            v-for="option in CONTENT_BOX_BORDER_STYLES"
            :key="option"
            type="button"
            class="border-input__style"
            :class="{ 'border-input__style--active': option === borderStyle }"
            :title="$t('custom.border_input.style_title', { style: option })"
            @click="$emit('update:borderStyle', option)">
            <span v-if="'none' === option" class="border-input__cross" />
            <span v-else class="border-input__sample" :style="{ borderTopStyle: option }" />
          </button>
        </div>

        <div class="border-input__length" :class="{ 'border-input__length--off': lengthOff }">
          <span class="border-input__label">{{ $t("custom.border_input.length") }}</span>
          <input
            class="border-input__number border-input__number--plain"
            type="number"
            min="0"
            max="100"
            :disabled="lengthOff"
            :value="length"
            :title="$t('custom.border_input.length_title')"
            @input="writeLength($event.target)" />
          <span class="border-input__unit">%</span>
        </div>
      </div>

      <div class="border-input__color">
        <VaColorPicker :position="'top'" :value="borderColor" @input="$emit('update:borderColor', $event)" />
      </div>
    </div>

    <div class="border-input__box">
      <span class="border-input__frame" />
      <button
        type="button"
        class="border-input__link"
        :disabled="isOff"
        :title="linked ? $t('custom.border_input.link_separate') : $t('custom.border_input.link_shared')"
        @click="toggleLink">
        <v-icon small>{{ linked ? "mdi-link-variant" : "mdi-link-variant-off" }}</v-icon>
      </button>
      <input
        v-for="side in SIDES"
        :key="side.key"
        class="border-input__number"
        :class="[`border-input__number--${side.key}`, { 'border-input__number--linked': linked && !isOff }]"
        type="number"
        min="0"
        :disabled="isOff"
        :value="borderWidth[side.key]"
        :title="$t('custom.border_input.side_width', { side: $t(`custom.sides.${side.key}`) })"
        @input="write(side.key, $event.target.value)" />
    </div>
  </div>
</template>

<script>
  import { CONTENT_BOX_BORDER_STYLES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";

  const SIDES = [{ key: "top" }, { key: "right" }, { key: "bottom" }, { key: "left" }];

  export default {
    name: "VaBorderInput",
    components: { VaColorPicker },
    props: {
      borderStyle: { type: String, default: "solid" },
      borderColor: { type: String, default: "#2454C7" },
      borderWidth: { type: Object, required: true },
      length: { type: Number, default: 100 },
      linked: { type: Boolean, default: false },
    },
    data() {
      return { CONTENT_BOX_BORDER_STYLES, SIDES };
    },
    computed: {
      isOff() {
        return "none" === this.borderStyle;
      },
      lengthOff() {
        return this.isOff || SIDES.every(side => !(this.borderWidth[side.key] > 0));
      },
    },
    methods: {
      write(side, value) {
        const width = Math.max(0, Number.parseFloat(value) || 0);
        const keys = this.linked ? SIDES.map(item => item.key) : [side];

        this.emitWidth(keys, width);
      },
      writeLength(input) {
        const parsed = Number.parseFloat(input.value);

        if (!Number.isFinite(parsed)) return;

        const length = Math.min(100, Math.max(0, parsed));

        if (length !== parsed) input.value = length;
        this.$emit("update:length", length);
      },
      toggleLink() {
        const linked = !this.linked;

        this.$emit("update:linked", linked);

        if (linked) {
          this.emitWidth(
            SIDES.map(side => side.key),
            this.borderWidth.top,
          );
        }
      },
      emitWidth(keys, width) {
        const next = { ...this.borderWidth };

        keys.forEach(key => {
          next[key] = width;
        });

        this.$emit("update:borderWidth", next);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  $frame-inset-y: 24px;
  $frame-inset-x: 40px;

  .border-input {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px 24px;

    &--off {
      .border-input__color,
      .border-input__box {
        opacity: 0.45;
        pointer-events: none;
      }
    }

    &__controls {
      display: flex;
      flex-direction: column;
      gap: 12px;
      flex: 0 0 auto;
    }

    &__head {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__label {
      @include text-caption;

      color: get-color("subtitle");
    }

    &__styles {
      display: flex;
      flex: 0 0 auto;
      border: 1px solid get-color("border");
      border-radius: 4px;
      overflow: hidden;
    }

    &__style {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 34px;
      padding: 0;
      border: 0;
      background-color: get-color("white");
      cursor: pointer;

      & + & {
        border-left: 1px solid get-color("border");
      }

      &:hover {
        background-color: get-color("element-bg-200");
      }

      &--active {
        background-color: get-color("primary-10-hover");
      }
    }

    &__sample {
      width: 24px;
      border-top: 2px solid get-color("body");
    }

    &__cross {
      width: 12px;
      height: 12px;
      background: linear-gradient(
          to bottom right,
          transparent calc(50% - 1px),
          get-color("body") calc(50% - 1px),
          get-color("body") calc(50% + 1px),
          transparent calc(50% + 1px)
        ),
        linear-gradient(
          to bottom left,
          transparent calc(50% - 1px),
          get-color("body") calc(50% - 1px),
          get-color("body") calc(50% + 1px),
          transparent calc(50% + 1px)
        );
    }

    &__box {
      position: relative;
      flex: 0 1 320px;
      height: 148px;
    }

    &__frame {
      position: absolute;
      top: $frame-inset-y;
      right: $frame-inset-x;
      bottom: $frame-inset-y;
      left: $frame-inset-x;
      border: 1px solid get-color("border");
      border-radius: 2px;
    }

    &__link {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 4px;
      border: 0;
      background: none;
      cursor: pointer;
      opacity: 0.45;

      &:hover {
        opacity: 1;
      }
    }

    &__number {
      @include text-caption;

      width: 50px;
      height: 30px;
      padding: 0 8px;
      text-align: center;
      border: 1px solid get-color("border");
      border-radius: 4px;
      background-color: get-color("white");
      outline: none;
      appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        appearance: none;
        margin: 0;
      }

      &:focus {
        border-color: get-color("primary");
      }

      &:disabled {
        color: get-color("disabled");
        background-color: get-color("hover");
      }

      &--linked {
        color: get-color("primary");
      }

      &--top,
      &--right,
      &--bottom,
      &--left {
        position: absolute;
      }

      &--top {
        top: $frame-inset-y;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &--bottom {
        bottom: $frame-inset-y;
        left: 50%;
        transform: translate(-50%, 50%);
      }

      &--left {
        top: 50%;
        left: $frame-inset-x;
        transform: translate(-50%, -50%);
      }

      &--right {
        top: 50%;
        right: $frame-inset-x;
        transform: translate(50%, -50%);
      }

      &--plain {
        text-align: left;
      }
    }

    &__color {
      ::v-deep .v-text-field {
        padding-top: 0;
      }

      ::v-deep .v-input__slot {
        min-height: 34px;
        max-height: 34px;
      }

      ::v-deep .v-input__append-inner {
        align-self: center;
        margin-top: 0;
      }

      ::v-deep .field-picker__color-preview {
        width: 18px;
        height: 18px;
      }
    }

    &__length {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 0 0 auto;

      &--off {
        opacity: 0.45;
      }
    }

    &__unit {
      @include text-caption;

      color: get-color("subtitle");
    }
  }
</style>
